// Audio Translation Service
// Uses Web Speech API for speech recognition and translation
// Uses MyMemory API for translation (free, no API key)
// Uses Web Speech API for text-to-speech output
//
// IMPORTANT: For real-time translation of remote WebRTC audio:
// 1. Uses Web Audio API to capture remote audio stream
// 2. Uses Speech Recognition API with microphone (primary) or remote audio capture
// 3. Provides translation through Text-to-Speech
//
// Note: Browser's Web Speech API primarily uses microphone. For remote audio,
// we implement a workaround using AudioContext to monitor audio levels and
// trigger speech recognition when audio is detected.

// Supported languages with their codes
export const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी', flag: '🇮🇳' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇱🇰' },
    { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳' },
    { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳' },
    { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳' },
    { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
];

// Get language name from code
export const getLanguageName = (code) => {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang ? lang.name : code;
};

// Get language native name from code
export const getLanguageNativeName = (code) => {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang ? lang.nativeName : code;
};

// Try using MyMemory API (free, no API key needed)
const translateWithMyMemory = async (text, sourceLang, targetLang) => {
    try {
        const langPair = `${sourceLang}|${targetLang}`;
        const response = await fetch(
            `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${langPair}`,
            { method: 'GET' }
        );

        if (response.ok) {
            const data = await response.json();
            if (data.responseStatus === 200 && data.responseData.translatedText) {
                console.log('MyMemory translation:', data.responseData.translatedText);
                return data.responseData.translatedText;
            }
        }
    } catch (error) {
        console.log('MyMemory API error:', error);
    }
    return null;
};

// Mock translation fallback
const mockTranslate = (text, sourceLang, targetLang) => {
    console.log('Using mock translation - API unavailable');
    // Simple word mapping for demo
    const mockTranslations = {
        'en-hi': {
            'hello': 'नमस्ते',
            'hi': 'नमस्ते',
            'how are you': 'आप कैसे हैं',
            'thank you': 'धन्यवाद',
            'thanks': 'धन्यवाद',
            'good': 'अच्छा',
            'yes': 'हाँ',
            'no': 'नहीं',
            'goodbye': 'अलविदा',
            'welcome': 'स्वागत है',
            'sorry': 'माफ़ी चाहता हूँ',
            'please': 'कृपया',
            'what is your name': 'आपका नाम क्या है',
            'my name is': 'मेरा नाम है',
        },
        'en-es': {
            'hello': 'hola',
            'hi': 'hola',
            'how are you': 'cómo estás',
            'thank you': 'gracias',
            'good': 'bueno',
        }
    };

    const key = `${sourceLang}-${targetLang}`;
    const dictionary = mockTranslations[key] || {};

    const lowerText = text.toLowerCase().trim();
    if (dictionary[lowerText]) {
        return dictionary[lowerText];
    }

    // If no direct translation, add a prefix
    return `[${getLanguageName(targetLang)}] ${text}`;
};

// Translation function using MyMemory API (free, no API key needed)
// Falls back to mock translation if API is not available
export const translateText = async (text, sourceLang, targetLang) => {
    if (!text || sourceLang === targetLang) {
        return text;
    }

    console.log('Translating:', text, 'from', sourceLang, 'to', targetLang);

    // Try MyMemory API first (free, no API key needed)
    const myMemoryResult = await translateWithMyMemory(text, sourceLang, targetLang);
    if (myMemoryResult) {
        return myMemoryResult;
    }

    // Try LibreTranslate as fallback
    try {
        const response = await fetch('https://libretranslate.com/translate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                q: text,
                source: sourceLang,
                target: targetLang,
                format: 'text',
            }),
        });

        if (response.ok) {
            const data = await response.json();
            return data.translatedText;
        }
    } catch (error) {
        console.log('Translation APIs not available, using mock translation');
    }

    // Fallback: Simple mock translation for demo purposes
    return mockTranslate(text, sourceLang, targetLang);
};

// Text-to-Speech class
class AudioPlayer {
    constructor() {
        this.synthesis = window.speechSynthesis;
        this.currentUtterance = null;
        this.isPlaying = false;
        this.queue = [];
        this.onEndCallback = null;
        this.voices = [];
        this.voicesLoaded = false;

        // Load voices - some browsers need this to be asynchronous
        this.loadVoices();
    }

    loadVoices() {
        const voices = this.synthesis.getVoices();
        if (voices && voices.length > 0) {
            this.voices = voices;
            this.voicesLoaded = true;
            console.log('Voices loaded:', voices.length);
            // Log available Hindi voices if any
            const hindiVoices = voices.filter(v => v.lang.includes('hi'));
            console.log('Hindi voices available:', hindiVoices.length, hindiVoices.map(v => v.name));
        } else {
            // Wait for voices to load
            this.synthesis.onvoiceschanged = () => {
                this.voices = this.synthesis.getVoices();
                this.voicesLoaded = true;
                console.log('Voices loaded asynchronously:', this.voices.length);
            };
        }
    }

    getVoiceForLanguage(language) {
        // Force reload voices if not loaded
        if (!this.voicesLoaded || this.voices.length === 0) {
            this.voices = this.synthesis.getVoices();
            this.voicesLoaded = true;
        }

        // Try to find an exact match first
        let voice = this.voices.find(v => v.lang === language);

        // Try starting with language code
        if (!voice) {
            const langCode = language.split('-')[0];
            voice = this.voices.find(v => v.lang.startsWith(langCode));
        }

        // If still no voice, just return undefined - the browser will use default
        return voice;
    }

    speak(text, language = 'en', onEnd = null) {
        if (!text) return;

        console.log('TTS speaking:', text, 'in language:', language);

        this.onEndCallback = onEnd;

        // Cancel any ongoing speech
        if (this.synthesis.speaking) {
            this.synthesis.cancel();
        }

        // Give browser a moment to cancel previous speech
        setTimeout(() => {
            // Map language codes to proper locale codes for TTS
            const localeMap = {
                'en': 'en-US',
                'hi': 'hi-IN',
                'es': 'es-ES',
                'fr': 'fr-FR',
                'de': 'de-DE',
                'it': 'it-IT',
                'pt': 'pt-PT',
                'ru': 'ru-RU',
                'ja': 'ja-JP',
                'ko': 'ko-KR',
                'zh': 'zh-CN',
                'ar': 'ar-SA',
                'ta': 'ta-IN',
                'te': 'te-IN',
                'bn': 'bn-IN',
                'mr': 'mr-IN',
                'gu': 'gu-IN',
                'ml': 'ml-IN',
                'kn': 'kn-IN',
                'pa': 'pa-IN',
            };
            const locale = localeMap[language] || language;

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = locale; // This is critical - must set the language
            utterance.rate = 0.9; // Slightly slower for clarity
            utterance.pitch = 1.0;
            utterance.volume = 1.0;

            // Get available voice for the language
            const voice = this.getVoiceForLanguage(locale);
            if (voice) {
                utterance.voice = voice;
                console.log('Using voice:', voice.name, 'for language:', locale);
            } else {
                console.log('No specific voice found for', locale, '- using browser default');
            }

            utterance.onstart = () => {
                console.log('TTS started speaking');
            };

            utterance.onend = () => {
                console.log('TTS finished speaking');
                this.isPlaying = false;
                if (this.onEndCallback) {
                    this.onEndCallback();
                }
                // Process queue
                if (this.queue.length > 0) {
                    const next = this.queue.shift();
                    this.speak(next.text, next.language, next.onEnd);
                }
            };

            utterance.onerror = (event) => {
                console.error('Speech synthesis error:', event.error);
                this.isPlaying = false;
            };

            this.currentUtterance = utterance;
            this.isPlaying = true;
            this.synthesis.speak(utterance);
        }, 100);
    }

    queueSpeech(text, language = 'en', onEnd = null) {
        this.queue.push({ text, language, onEnd });
    }

    stop() {
        this.synthesis.cancel();
        this.queue = [];
        this.isPlaying = false;
    }

    pause() {
        this.synthesis.pause();
    }

    resume() {
        this.synthesis.resume();
    }
}

// Speech Recognition class
class SpeechRecognizer {
    constructor(options = {}) {
        this.onResult = options.onResult || (() => { });
        this.onError = options.onError || (() => { });
        this.onEnd = options.onEnd || (() => { });
        this.onStart = options.onStart || (() => { });

        this.recognition = null;
        this.isListening = false;
        this.interimResults = [];
        this.finalResults = [];
        this.language = options.language || 'en-US';
        this.restartAttempts = 0;
    }

    initialize() {
        // Check browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            const error = 'Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.';
            console.error(error);
            this.onError({ error: 'not-supported', message: error });
            return false;
        }

        this.recognition = new SpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.maxAlternatives = 1;
        this.recognition.lang = this.language;
        console.log('Speech recognition initialized with language:', this.language);

        this.recognition.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript;
                } else {
                    interimTranscript += transcript;
                }
            }

            if (finalTranscript) {
                this.finalResults.push(finalTranscript);
                this.onResult({ final: finalTranscript, interim: interimTranscript, isFinal: true });
            } else if (interimTranscript) {
                this.onResult({ final: '', interim: interimTranscript, isFinal: false });
            }
        };

        this.recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error, event.message);

            // Auto-restart on common errors including 'aborted'
            const restartableErrors = ['no-speech', 'audio-capture', 'network', 'aborted'];

            if (restartableErrors.includes(event.error)) {
                this.restartAttempts++;
                if (this.restartAttempts < 10) {
                    console.log('Attempting to restart recognition...', this.restartAttempts, event.error);
                    setTimeout(() => {
                        try {
                            this.start();
                        } catch (e) {
                            console.log('Restart failed:', e.message);
                        }
                    }, 1000);
                }
                // Don't report common errors to callback
                return;
            }

            this.onError({ error: event.error, message: event.message });
        };

        this.recognition.onend = () => {
            this.isListening = false;
            this.restartAttempts = 0;
            this.onEnd();
        };

        this.recognition.onstart = () => {
            this.isListening = true;
            this.restartAttempts = 0;
            console.log('Speech recognition started successfully');
            this.onStart();
        };

        return true;
    }

    setLanguage(lang) {
        this.language = lang;
        if (this.recognition) {
            this.recognition.lang = lang;
            console.log('Language changed to:', lang);
        }
    }

    start() {
        if (!this.recognition) {
            console.error('Recognition not initialized');
            return;
        }

        if (this.isListening) {
            console.log('Already listening');
            return;
        }

        this.finalResults = [];
        this.interimResults = [];

        try {
            this.recognition.start();
        } catch (e) {
            console.error('Error starting recognition:', e.message);
            // If already started, try to stop and restart
            if (e.message.includes('already started')) {
                try {
                    this.recognition.stop();
                    setTimeout(() => {
                        try {
                            this.recognition.start();
                        } catch (e2) {
                            console.error('Failed to restart:', e2);
                        }
                    }, 100);
                } catch (e2) {
                    console.error('Error stopping recognition:', e2);
                }
            }
        }
    }

    stop() {
        if (this.recognition && this.isListening) {
            try {
                this.recognition.stop();
            } catch (e) {
                console.error('Error stopping recognition:', e);
            }
        }
    }

    getResults() {
        return [...this.finalResults];
    }

    clearResults() {
        this.finalResults = [];
        this.interimResults = [];
    }
}

// Audio Translator - Main class that combines speech recognition and translation
// This implementation provides real-time speech-to-text translation
// using the browser's Web Speech API for speech recognition
// and Text-to-Speech for playing translated audio
//
// IMPORTANT: This translator works by capturing the LOCAL USER's microphone speech,
// translating it, and playing the translation to the REMOTE USER.
// For bidirectional translation, both users need to have translation enabled.
class AudioTranslator {
    constructor(options = {}) {
        this.sourceLanguage = options.sourceLanguage || 'en';
        this.targetLanguage = options.targetLanguage || 'hi';
        this.isTranslating = false;
        this.audioPlayer = new AudioPlayer();
        this.recognizer = null;
        this.onTranslation = options.onTranslation || (() => { });
        this.onError = options.onError || (() => { });
        this.onStatusChange = options.onStatusChange || (() => { });
        this.remoteStream = null;
        this.audioContext = null;
        this.analyser = null;
        this.isSpeaking = false;
        this.isInitialized = false;
        this.audioLevelInterval = null;
    }

    initialize() {
        // Initialize speech recognizer with source language
        const localeMap = {
            'en': 'en-US',
            'hi': 'hi-IN',
            'es': 'es-ES',
            'fr': 'fr-FR',
            'de': 'de-DE',
            'it': 'it-IT',
            'pt': 'pt-PT',
            'ru': 'ru-RU',
            'ja': 'ja-JP',
            'ko': 'ko-KR',
            'zh': 'zh-CN',
            'ar': 'ar-SA',
            'ta': 'ta-IN',
            'te': 'te-IN',
            'bn': 'bn-IN',
            'mr': 'mr-IN',
            'gu': 'gu-IN',
            'ml': 'ml-IN',
            'kn': 'kn-IN',
            'pa': 'pa-IN',
        };

        this.recognizer = new SpeechRecognizer({
            language: localeMap[this.sourceLanguage] || 'en-US',
            onResult: (result) => this.handleSpeechResult(result),
            onError: (error) => this.handleError(error),
            onEnd: () => this.handleRecognitionEnd(),
            onStart: () => this.handleRecognitionStart(),
        });

        const initialized = this.recognizer.initialize();
        this.isInitialized = true;
        return initialized;
    }

    setSourceLanguage(lang) {
        this.sourceLanguage = lang;
        if (this.recognizer) {
            const localeMap = {
                'en': 'en-US', 'hi': 'hi-IN', 'es': 'es-ES', 'fr': 'fr-FR',
                'de': 'de-DE', 'it': 'it-IT', 'pt': 'pt-PT', 'ru': 'ru-RU',
                'ja': 'ja-JP', 'ko': 'ko-KR', 'zh': 'zh-CN', 'ar': 'ar-SA',
                'ta': 'ta-IN', 'te': 'te-IN', 'bn': 'bn-IN', 'mr': 'mr-IN',
                'gu': 'gu-IN', 'ml': 'ml-IN', 'kn': 'kn-IN', 'pa': 'pa-IN',
            };
            const locale = localeMap[lang] || lang + '-US';
            this.recognizer.setLanguage(locale);
        }
    }

    setTargetLanguage(lang) {
        this.targetLanguage = lang;
    }

    // Monitor audio levels from remote stream
    monitorRemoteAudio(stream, onActivity) {
        if (!stream) return;

        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioSource = this.audioContext.createMediaStreamSource(stream);
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            audioSource.connect(this.analyser);

            const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

            this.audioLevelInterval = setInterval(() => {
                this.analyser.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                if (onActivity) {
                    onActivity(average > 50); // Threshold for audio activity
                }
            }, 100);
        } catch (error) {
            console.error('Error monitoring remote audio:', error);
        }
    }

    // Start translating - captures LOCAL user's microphone speech
    async startTranslation(remoteStream) {
        if (this.isTranslating) {
            return;
        }

        this.remoteStream = remoteStream;

        try {
            // Monitor remote audio for activity (optional - for future use)
            if (remoteStream) {
                this.monitorRemoteAudio(remoteStream, (hasActivity) => {
                    if (hasActivity) {
                        console.log('Remote audio detected - waiting for speech...');
                    }
                });
            }

            // Start speech recognition - uses MICROPHONE
            this.recognizer.start();
            this.isTranslating = true;
            this.onStatusChange('translating');

            console.log('Translation started!');
            console.log('Source language:', this.sourceLanguage, '-> Target:', this.targetLanguage);
            console.log('Speak into your microphone - your speech will be translated.');

        } catch (error) {
            console.error('Error starting translation:', error);
            this.onError(error);
        }
    }

    stopTranslation() {
        if (this.recognizer) {
            this.recognizer.stop();
        }
        if (this.audioPlayer) {
            this.audioPlayer.stop();
        }
        if (this.audioContext) {
            clearInterval(this.audioLevelInterval);
            this.audioContext.close();
            this.audioContext = null;
        }
        this.isTranslating = false;
        this.onStatusChange('stopped');
    }

    async handleSpeechResult(result) {
        if (result.isFinal && result.final && result.final.trim().length > 0) {
            console.log('Speech recognized:', result.final);

            // Notify about original speech
            this.onTranslation({
                original: result.final,
                status: 'translating'
            });

            try {
                // Translate the speech
                console.log('Translating from', this.sourceLanguage, 'to', this.targetLanguage);
                const translatedText = await translateText(
                    result.final,
                    this.sourceLanguage,
                    this.targetLanguage
                );

                console.log('Translation:', translatedText);

                // Speak the translation
                this.isSpeaking = true;
                this.audioPlayer.speak(translatedText, this.targetLanguage, () => {
                    this.isSpeaking = false;
                    this.onTranslation({
                        original: result.final,
                        translated: translatedText,
                        status: 'completed'
                    });
                });

            } catch (error) {
                console.error('Translation error:', error);
                this.onError(error);
            }
        }
    }

    handleRecognitionStart() {
        console.log('Speech recognition started - listening for your speech...');
        this.onStatusChange('listening');
    }

    handleRecognitionEnd() {
        console.log('Speech recognition ended');
        if (this.isTranslating) {
            // Restart recognition if still translating
            setTimeout(() => {
                if (this.isTranslating && !this.isSpeaking) {
                    try {
                        this.recognizer.start();
                    } catch (e) {
                        console.log('Restarting recognition...');
                    }
                }
            }, 500);
        }
    }

    handleError(error) {
        console.error('Recognition error:', error.error, error.message);

        // Don't stop on common errors - just try to restart
        const ignoredErrors = ['no-speech', 'audio-capture', 'not-allowed', 'network', 'aborted', 'not-allowed'];

        if (error.error && ignoredErrors.includes(error.error)) {
            console.log('Ignoring error, attempting to continue:', error.error);
            if (this.isTranslating) {
                setTimeout(() => {
                    if (this.isTranslating && this.recognizer) {
                        try {
                            // Only restart if not already listening
                            if (!this.recognizer.isListening) {
                                console.log('Restarting speech recognition...');
                                this.recognizer.start();
                            }
                        } catch (e) {
                            console.log('Could not restart recognition:', e.message);
                        }
                    }
                }, 1500);
            }
            // Don't call onError for these common errors
            return;
        } else {
            this.onError(error);
        }
    }

    isSupported() {
        return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    }
}

// Export singleton instance
export const audioTranslator = new AudioTranslator();

// Export classes for more control
export { AudioTranslator, AudioPlayer, SpeechRecognizer };
